// Screenshot carousel component for product showcase
import { open as openLightbox } from './imageLightbox.js';

export class ScreenshotCarousel {
  constructor(container, options = {}) {
    this.container = container;
    this.lang = options.lang || 'en';
    this.fallbackLang = options.fallbackLang || 'en';
    this.firstIndex = Number.isFinite(options.firstIndex) ? options.firstIndex : 2;
    this.count = Number.isFinite(options.count) ? options.count : 5;
    this.currentIndex = 0;
    this.autoRotateInterval = null;
    this.screenshots = Array.from({ length: this.count }, (_, i) => {
      const screenshotIndex = this.firstIndex + i;
      return {
        index: screenshotIndex,
        alt: 'Gantty product screenshot',
      };
    });
    
    this.init();
  }

  getScreenshotUrl(lang, index) {
    return `/screenshots/${lang}/${lang}_${index}.webp`;
  }

  applyFallbackToImage(img, screenshotIndex) {
    const placeholder = img.nextElementSibling;
    if (placeholder && placeholder.classList.contains('showcase-placeholder')) {
      placeholder.style.display = 'none';
    }

    const primarySrc = this.getScreenshotUrl(this.lang, screenshotIndex);
    const fallbackSrc = this.getScreenshotUrl(this.fallbackLang, screenshotIndex);

    let triedFallback = false;

    img.addEventListener('load', () => {
      if (placeholder && placeholder.classList.contains('showcase-placeholder')) {
        placeholder.style.display = 'none';
      }
      img.style.display = '';
    });

    img.addEventListener('error', () => {
      if (!triedFallback) {
        triedFallback = true;
        img.src = fallbackSrc;
        return;
      }

      img.style.display = 'none';
      if (placeholder && placeholder.classList.contains('showcase-placeholder')) {
        placeholder.style.display = 'flex';
      }
    });

    img.src = primarySrc;
  }

  init() {
    this.render();
    this.startAutoRotate();
    
    // Pause on hover
    this.container.addEventListener('mouseenter', () => this.stopAutoRotate());
    this.container.addEventListener('mouseleave', () => this.startAutoRotate());
  }

  render() {
    const html = `
      <div class="screenshot-carousel-wrapper">
        ${this.screenshots.map((screenshot, index) => `
          <div class="screenshot-slide ${index === 0 ? 'active' : ''}" data-index="${index}">
            <div class="showcase-screenshot">
              <img alt="${screenshot.alt}"
                   class="showcase-image"
                   data-index="${index}"
                   data-screenshot-index="${screenshot.index}"
                   width="1200" 
                   height="800" 
                   loading="lazy" />
              <div class="showcase-placeholder" style="display:none">
                <div class="placeholder-content">
                  <svg width="120" height="120" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <path d="M9 9h6v6H9z" />
                  </svg>
                  <p>Product Screenshot</p>
                  <p class="placeholder-hint">Add screenshot: 1200x800px</p>
                </div>
              </div>
            </div>
          </div>
        `).join('')}
      </div>
      <div class="carousel-controls">
        <button class="btn btn-secondary" id="prevScreenshot" aria-label="Previous screenshot">‹</button>
        <button class="btn btn-secondary" id="nextScreenshot" aria-label="Next screenshot">›</button>
      </div>
      <div class="carousel-dots">
        ${this.screenshots.map((_, index) => `
          <button class="carousel-dot ${index === 0 ? 'active' : ''}" 
                  data-index="${index}" 
                  aria-label="Go to screenshot ${index + 1}"></button>
        `).join('')}
      </div>
    `;
    
    this.container.innerHTML = html;

    // Apply localized src + fallback logic
    this.container.querySelectorAll('img.showcase-image').forEach((img) => {
      const screenshotIndex = Number(img.getAttribute('data-screenshot-index'));
      if (!Number.isFinite(screenshotIndex)) return;
      this.applyFallbackToImage(img, screenshotIndex);
    });
    
    // Event listeners
    document.getElementById('prevScreenshot')?.addEventListener('click', () => this.prev());
    document.getElementById('nextScreenshot')?.addEventListener('click', () => this.next());
    
    this.container.querySelectorAll('.carousel-dot').forEach((dot, index) => {
      dot.addEventListener('click', () => this.goTo(index));
    });

    this.container.querySelectorAll('.showcase-image').forEach((img) => {
      img.addEventListener('click', () => {
        if (img.src) {
          this.stopAutoRotate();
          openLightbox(img.src, img.alt);
        }
      });
    });
  }

  show(index) {
    const slides = this.container.querySelectorAll('.screenshot-slide');
    const dots = this.container.querySelectorAll('.carousel-dot');
    
    slides.forEach((slide, i) => {
      slide.classList.toggle('active', i === index);
    });
    
    dots.forEach((dot, i) => {
      dot.classList.toggle('active', i === index);
    });
    
    this.currentIndex = index;
  }

  next() {
    const nextIndex = (this.currentIndex + 1) % this.screenshots.length;
    this.show(nextIndex);
  }

  prev() {
    const prevIndex = (this.currentIndex - 1 + this.screenshots.length) % this.screenshots.length;
    this.show(prevIndex);
  }

  goTo(index) {
    this.show(index);
  }

  startAutoRotate() {
    this.stopAutoRotate();
    this.autoRotateInterval = setInterval(() => {
      this.next();
    }, 6000); // 6 seconds for screenshots
  }

  stopAutoRotate() {
    if (this.autoRotateInterval) {
      clearInterval(this.autoRotateInterval);
      this.autoRotateInterval = null;
    }
  }
}

